import { LinkPreview, RewardPreview } from "./types";

const EPHEMERAL_URL_PATTERN = /^(blob:|data:|file:|about:|javascript:|webkit-fake-url:)/i;

export function sanitizePersistentPreviewUrl(url?: string): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed || EPHEMERAL_URL_PATTERN.test(trimmed)) return undefined;
  return trimmed;
}

function normalizePreviewUrl(url?: string): string | undefined {
  const trimmed = sanitizePersistentPreviewUrl(url);
  if (!trimmed) return undefined;
  return trimmed.startsWith("http://") || trimmed.startsWith("https://") ? trimmed : `https://${trimmed}`;
}

export function getRewardPreviewHostname(url?: string): string | undefined {
  const normalized = normalizePreviewUrl(url);
  if (!normalized) return undefined;

  try {
    return new URL(normalized).hostname;
  } catch {
    return undefined;
  }
}

export function getRewardPreviewDomain(url?: string): string | undefined {
  return getRewardPreviewHostname(url)?.replace(/^www\./, "");
}

export function getRewardPreviewFavicon(url?: string): string | undefined {
  const hostname = getRewardPreviewHostname(url);
  return hostname
    ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=128`
    : undefined;
}

export function buildRewardPreview(
  linkPreview?: Partial<LinkPreview> | null,
  fallbackUrl?: string,
): RewardPreview | undefined {
  const url = normalizePreviewUrl(linkPreview?.url) ?? normalizePreviewUrl(fallbackUrl);
  if (!url) return undefined;

  const favicon = sanitizePersistentPreviewUrl(linkPreview?.favicon) ?? getRewardPreviewFavicon(url);
  const icon = sanitizePersistentPreviewUrl(linkPreview?.icon) ?? favicon;
  const logo = sanitizePersistentPreviewUrl(linkPreview?.logo) ?? icon ?? favicon;

  return {
    title: linkPreview?.title?.trim() || undefined,
    domain: linkPreview?.domain?.trim() || getRewardPreviewDomain(url),
    url,
    image: sanitizePersistentPreviewUrl(linkPreview?.image),
    logo,
    icon,
    favicon,
  };
}

export function resolveRewardPreview(
  rewardPreview?: RewardPreview | null,
  linkPreview?: Partial<LinkPreview> | null,
  fallbackUrl?: string,
): RewardPreview | undefined {
  const derivedPreview = buildRewardPreview(linkPreview, fallbackUrl);
  if (!rewardPreview) return derivedPreview;

  const url = normalizePreviewUrl(rewardPreview.url) ?? derivedPreview?.url ?? normalizePreviewUrl(fallbackUrl);
  if (!url) return undefined;

  const favicon = sanitizePersistentPreviewUrl(rewardPreview.favicon) ?? derivedPreview?.favicon ?? getRewardPreviewFavicon(url);
  const icon = sanitizePersistentPreviewUrl(rewardPreview.icon) ?? derivedPreview?.icon ?? favicon;
  const logo = sanitizePersistentPreviewUrl(rewardPreview.logo) ?? derivedPreview?.logo ?? icon ?? favicon;

  return {
    title: rewardPreview.title?.trim() || derivedPreview?.title,
    domain: rewardPreview.domain?.trim() || derivedPreview?.domain || getRewardPreviewDomain(url),
    url,
    image: sanitizePersistentPreviewUrl(rewardPreview.image) ?? derivedPreview?.image,
    logo,
    icon,
    favicon,
  };
}

export function getRewardPreviewAssets(preview?: RewardPreview | null) {
  const candidates = [
    preview?.image ? { src: preview.image, kind: "image" as const } : null,
    preview?.logo ? { src: preview.logo, kind: "logo" as const } : null,
    preview?.icon ? { src: preview.icon, kind: "icon" as const } : null,
    preview?.favicon ? { src: preview.favicon, kind: "favicon" as const } : null,
  ].filter((asset): asset is { src: string; kind: "image" | "logo" | "icon" | "favicon" } => Boolean(asset));

  const seen = new Set<string>();
  return candidates.filter((asset) => {
    if (seen.has(asset.src)) return false;
    seen.add(asset.src);
    return true;
  });
}
